/**
 * Whether an ingredient row should offer a brand control at all.
 *
 * A picker is only honest when there is something to pick. With one approved
 * brand the answer is already decided; with none there is nothing to decide.
 * The old build showed a chevron either way, so a chef tapped into a menu to
 * discover it held a single pre-ticked row — or a sentence explaining the
 * menu was empty.
 *
 * Kept out of JSX so the rule is testable on its own.
 */
export type BrandControlMode = 'none' | 'text' | 'picker'

export function brandControlMode(input: {
  /** Live brand options for this ingredient. Undefined on surfaces that carry
   *  no catalog ids (the Recipes drawer) — those read as plain text. */
  brandOptionCount?: number | null
  /**
   * Retained for callers that still send it, but no longer suppresses the
   * picker. A recipe-line pin used to fix the brand for everyone; product
   * decision 2026-09-11 is that a brand is ALWAYS the kitchen's to choose.
   */
  brandSelectable?: boolean
  /** Whether we have a brand name to show at all. */
  hasBrandLabel: boolean
  /** Whether this row is even tied to a catalog ingredient. */
  hasIngredientId: boolean
  /** Whether we know which kitchen is asking. */
  hasKitchen: boolean
}): BrandControlMode {
  // Nothing to attach a selection to, or nobody to attach it for.
  if (!input.hasIngredientId || !input.hasKitchen) {
    return input.hasBrandLabel ? 'text' : 'none'
  }
  const count = input.brandOptionCount ?? 0
  if (count >= 2) {
    return 'picker'
  }
  // One brand, or none: settled either way.
  return input.hasBrandLabel ? 'text' : 'none'
}
